import { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Start typing an address...',
  countryCode = null,
  type = 'address',
  required = false,
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function fetchSuggestions(q) {
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q,
        format: 'json',
        addressdetails: '1',
        limit: '6',
        ...(countryCode ? { countrycodes: countryCode } : {}),
        ...(type === 'city' ? { featuretype: 'city' } : {}),
      });

      const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
        headers: { 'Accept-Language': 'en', 'User-Agent': 'Transportily/1.0' },
      });
      const data = await res.json();

      const formatted = data.map(item => {
        if (type === 'city') {
          const city = item.address?.city || item.address?.town || item.address?.village || item.name;
          const country = item.address?.country;
          return { label: `${city}, ${country}`, value: city, full: item };
        } else {
          return { label: item.display_name, value: item.display_name, full: item };
        }
      }).filter((item, index, self) =>
        index === self.findIndex(t => t.value === item.value)
      );

      setSuggestions(formatted);
      setOpen(formatted.length > 0);
    } catch (e) {
      console.error('Autocomplete error:', e);
    }
    setLoading(false);
  }

  function handleInput(e) {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  }

  function handleSelect(suggestion) {
    setQuery(suggestion.value);
    onChange(suggestion.value);
    setSuggestions([]);
    setOpen(false);
  }

  return (
    <div className="ac-wrapper" ref={wrapperRef}>
      <input
        type="text"
        value={query}
        onChange={handleInput}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        onFocus={() => suggestions.length > 0 && setOpen(true)}
      />
      {loading && <div className="ac-loading">Searching...</div>}
      {open && suggestions.length > 0 && (
        <ul className="ac-dropdown">
          {suggestions.map((s, i) => (
            <li key={i} className="ac-item" onMouseDown={() => handleSelect(s)}>
              <span className="ac-icon">📍</span>
              {s.label}
            </li>
          ))}
          <li className="ac-footer">Powered by OpenStreetMap</li>
        </ul>
      )}
    </div>
  );
}